import { useGomakeAxios } from "@/hooks/use-gomake-axios";

export interface Ticket {
  id: number;
  ticketId: string;
  status: string;
  username: string;
  printHouseId: string;
  printHouseName: string;
  assignedTo?: string;
  assignedEmployeeId?: number;
  assignedEmployeeName?: string;
  priority: string;
  title: string;
  description?: string;
  summary?: string; // AI-generated summary for ticket tabs
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByUserId?: string;
  expectedDeliveryDate?: string; // Expected delivery date (ISO string format)
  /** Present when ticket is archived in support (hidden from default lists). */
  archived?: boolean;
  firstMessage?: string; // First message text (for quick preview)
  firstMessageSenderType?: string; // First message sender type
  firstMessageSenderName?: string; // First message sender name
  firstMessageCreatedAt?: string; // First message creation date (ISO string format)
}

export interface CreateTicketRequest {
  title: string;
  description?: string;
  priority: string;
  printHouseId?: string;
  printHouseName?: string;
  createdByUserId?: string;
  username?: string;
  email?: string;
}

export interface FileAttachment {
  id: number;
  messageId: number;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
}

export interface TicketMessage {
  id: number;
  ticketId: number;
  message: string;
  senderType: "user" | "support";
  senderId: string;
  senderName: string;
  senderEmail: string;
  createdAt: string;
  isInternal?: boolean;
  files?: FileAttachment[];
}

/** Exclude internal support notes from customer-visible message lists */
export const isCustomerVisibleMessage = (message: TicketMessage): boolean =>
  !message.isInternal;

interface GetTicketsFilters {
  status?: string;
  priority?: string;
  userId?: string;
  includeArchived?: boolean;
}

const TICKETS_LIST_DEDUPE_WINDOW_MS = 4000;
const ticketsListInFlightRequests = new Map<string, Promise<Ticket[]>>();
const ticketsListRecentResponses = new Map<
  string,
  { data: Ticket[]; expiresAt: number }
>();
const ticketNotificationsInFlightRequests = new Map<string, Promise<any>>();
const ticketNotificationsRecentResponses = new Map<
  string,
  { data: any; expiresAt: number }
>();

const buildTicketsListCacheKey = (filters?: GetTicketsFilters): string => {
  return JSON.stringify({
    status: filters?.status || "",
    priority: filters?.priority || "",
    userId: filters?.userId || "",
    includeArchived: filters?.includeArchived === true,
  });
};

export const invalidateTicketsListCache = () => {
  ticketsListInFlightRequests.clear();
  ticketsListRecentResponses.clear();
};

export const invalidateTicketNotificationsCache = () => {
  ticketNotificationsInFlightRequests.clear();
  ticketNotificationsRecentResponses.clear();
};

const getCachedTicketNotification = <T,>(
  cacheKey: string
): T | undefined => {
  const cachedResponse = ticketNotificationsRecentResponses.get(cacheKey);
  if (cachedResponse && cachedResponse.expiresAt > Date.now()) {
    return cachedResponse.data as T;
  }

  return undefined;
};

const getTicketNotification = async <T,>(
  callApi: any,
  cacheKey: string,
  endpoint: string,
  selector: (res: any) => T
): Promise<T> => {
  const cachedResponse = getCachedTicketNotification<T>(cacheKey);
  if (cachedResponse !== undefined) {
    return cachedResponse;
  }

  const inFlightRequest = ticketNotificationsInFlightRequests.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest as Promise<T>;
  }

  const requestPromise = (async () => {
    const res = await callApi("GET", endpoint);
    const data = selector(res);

    ticketNotificationsRecentResponses.set(cacheKey, {
      data,
      expiresAt: Date.now() + TICKETS_LIST_DEDUPE_WINDOW_MS,
    });

    return data;
  })();

  ticketNotificationsInFlightRequests.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    ticketNotificationsInFlightRequests.delete(cacheKey);
  }
};

export const getTickets = async (
  callApi: any,
  filters?: GetTicketsFilters
): Promise<Ticket[]> => {
  const cacheKey = buildTicketsListCacheKey(filters);
  const now = Date.now();
  const cachedResponse = ticketsListRecentResponses.get(cacheKey);

  if (cachedResponse && cachedResponse.expiresAt > now) {
    return cachedResponse.data;
  }

  const inFlightRequest = ticketsListInFlightRequests.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const params: any = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.priority) params.priority = filters.priority;
  if (filters?.userId) params.userId = filters.userId;
  if (filters?.includeArchived) params.includeArchived = "true";

  const requestPromise = (async () => {
    const res = await callApi("GET", "/api/tickets", params);
    const raw = res?.data;
    const data: Ticket[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.tickets)
          ? raw.tickets
          : [];

    ticketsListRecentResponses.set(cacheKey, {
      data,
      expiresAt: Date.now() + TICKETS_LIST_DEDUPE_WINDOW_MS,
    });

    return data;
  })();

  ticketsListInFlightRequests.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    ticketsListInFlightRequests.delete(cacheKey);
  }
};

export const getTicketById = async (
  callApi: any,
  ticketId: number
): Promise<Ticket> => {
  const res = await callApi("GET", `/api/tickets/${ticketId}`);
  return res?.data;
};

export const createTicket = async (
  callApi: any,
  ticketData: CreateTicketRequest
): Promise<Ticket> => {
  const res = await callApi("POST", "/api/tickets", ticketData);
  if (res?.success !== false) {
    invalidateTicketsListCache();
    invalidateTicketNotificationsCache();
  }
  return res?.data;
};

export const getTicketMessages = async (
  callApi: any,
  ticketId: number
): Promise<TicketMessage[]> => {
  const res = await callApi("GET", `/api/tickets/${ticketId}/messages`);
  const messages: TicketMessage[] = res?.data || [];
  return messages.filter(isCustomerVisibleMessage);
};

export const addTicketMessage = async (
  callApi: any,
  ticketId: number,
  message: string,
  senderType: "user" | "support",
  username?: string
): Promise<TicketMessage> => {
  const res = await callApi("POST", `/api/tickets/${ticketId}/messages`, {
    message,
    senderType,
    username,
  });
  if (res?.success !== false) {
    invalidateTicketsListCache();
    invalidateTicketNotificationsCache();
  }
  return res?.data;
};

/** Customer: set status to open when ticket is resolved or closed. */
export const reopenTicketAsOpen = async (
  callApi: any,
  ticketId: number
): Promise<Ticket> => {
  const res = await callApi("PATCH", `/api/tickets/${ticketId}/reopen`, {});
  if (!res || res.success === false) {
    const apiError =
      res?.errors?.error ||
      res?.msg ||
      res?.message ||
      "Failed to reopen ticket";
    throw new Error(apiError);
  }

  const ticket = res.data as Ticket | undefined;
  if (!ticket?.id) {
    throw new Error("Failed to reopen ticket");
  }

  invalidateTicketsListCache();
  invalidateTicketNotificationsCache();
  return ticket;
};

// Notification API functions
export const markTicketAsRead = async (
  callApi: any,
  ticketId: number
): Promise<void> => {
  const res = await callApi("POST", `/api/tickets/${ticketId}/mark-read`, {});
  if (res?.success !== false) {
    invalidateTicketsListCache();
    invalidateTicketNotificationsCache();
  }
};

export const getUnreadTicketCount = async (callApi: any): Promise<number> => {
  return await getTicketNotification<number>(
    callApi,
    "unread-count",
    "/api/tickets/unread-count",
    (res) => res?.data?.unreadCount || 0
  );
};

export const getUnreadTicketIds = async (callApi: any): Promise<number[]> => {
  return await getTicketNotification<number[]>(
    callApi,
    "unread-ids",
    "/api/tickets/unread-ids",
    (res) => res?.data?.ticketIds || []
  );
};

export const getFirstUnreadTicketId = async (
  callApi: any
): Promise<number | null> => {
  return await getTicketNotification<number | null>(
    callApi,
    "first-unread",
    "/api/tickets/first-unread",
    (res) => res?.data?.ticketId || null
  );
};

// File upload API functions
export const uploadFileToMessage = async (
  callApi: any,
  ticketId: number,
  messageId: number,
  file: File,
  onProgress?: (progress: number) => void
): Promise<FileAttachment> => {
  const axios = require("axios");
  const config = require("@/config").default;
  const { getUserToken } = require("@/services/storage-data");
  const { uploadFileResumable } = require("@/utils/resumable-upload");

  const userToken = getUserToken();
  const baseUrl = `${config.api_server}/api/tickets/${ticketId}/messages/${messageId}/files`;

  try {
    // Step 1: Request resumable upload URL from backend
    const urlResponse = await axios.post(
      `${baseUrl}/resumable-url`,
      {
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        fileSize: file.size,
      },
      {
        headers: {
          ...(userToken && { Authorization: `Bearer ${userToken}` }),
          "project-name": "business-dashboard",
          lang: "en",
          domain: "",
        },
      }
    );

    const { sessionUri, filePath, origin } = urlResponse.data;

    // Step 2: Upload file directly to Google Cloud Storage using resumable upload
    const uploadResult = await uploadFileResumable({
      sessionUri,
      file,
      onProgress,
      chunkSize: 8 * 1024 * 1024, // 8 MiB chunks
      origin, // Pass origin for CORS
    });

    if (!uploadResult.success) {
      throw new Error(uploadResult.error || "Upload failed");
    }

    // Step 3: Confirm upload completion with backend
    const confirmResponse = await axios.post(
      `${baseUrl}/confirm`,
      { filePath },
      {
        headers: {
          ...(userToken && { Authorization: `Bearer ${userToken}` }),
          "project-name": "business-dashboard",
          lang: "en",
          domain: "",
        },
      }
    );

    return confirmResponse.data;
  } catch (error: any) {
    console.error("Error uploading file:", error);
    throw error;
  }
};
